import type { LessonUnit } from "@mathos-v2/domain";
import {
  DEMO_PLACEHOLDER_SHA256,
  DEMO_SOURCE_ID_PREFIX,
} from "./curriculum-publication";

function source(page: number) {
  return {
    sourceRefVersion: "2" as const,
    sourceId: `${DEMO_SOURCE_ID_PREFIX}placeholder-p${page}`,
    bookId: "demo-ui-placeholder",
    sourceName: "界面演示占位内容（非《数学甲种本》教材）",
    sourcePdfSha256: null,
    pdfPage: null,
    printedPage: null,
    mappingStatus: "blocked" as const,
    reviewArtifactPath: `demo-ui-only/no-review-artifact/page-${page}.txt`,
    reviewArtifactSha256: DEMO_PLACEHOLDER_SHA256,
    imagePath: null,
    imageSha256: null,
    imageAssetStatus: "not_applicable" as const,
    reviewStatus: "rejected" as const,
    reviewedBy: null,
    reviewedAt: null,
  };
}

const setLesson: LessonUnit = {
  id: "L5",
  familyId: "sets",
  title: "集合与元素",
  topic: "用明确的对象语言认识集合",
  estimatedMinutes: 30,
  ageRange: [10, 15],
  approvalStatus: "blocked",
  approvalReason: "仅供界面演示；原教材、授权与 canonical 校订均未完成",
  knowledgeAtoms: [
    {
      id: "L5-definition",
      kind: "definition",
      title: "集合与元素",
      canonicalText:
        "把研究对象组成的整体称为集合，其中每一个对象称为这个集合的元素。",
      mathStatements: ["a\\in A", "a\\notin A"],
      sourceRefs: [source(12)],
      approvalStatus: "blocked",
    },
    {
      id: "L5-method",
      kind: "method",
      title: "判断属于关系",
      canonicalText:
        "判断一个具体对象是否属于集合，必须逐个核对对象，不能只凭共同特征猜测。",
      mathStatements: ["5\\notin\\{2,4,6,8,10\\}"],
      sourceRefs: [source(13)],
      approvalStatus: "blocked",
    },
    {
      id: "L5-misconception",
      kind: "misconception",
      title: "对象与集合混淆",
      canonicalText:
        "属于关系连接元素与集合，包含关系连接两个集合，二者不能混用。",
      mathStatements: ["a\\in A", "A\\subseteq B"],
      sourceRefs: [source(13)],
      approvalStatus: "blocked",
    },
  ],
  activities: [
    {
      id: "L5-explain",
      phase: "explain",
      title: "从对象出发",
      teacherIntent: "先讲清集合不是一句标签，而是一组能逐个核对的对象。",
      knowledgeAtomIds: ["L5-definition"],
      independence: "teacher_led",
      hints: [],
    },
    {
      id: "L5-model",
      phase: "model",
      title: "示范怎样核对",
      teacherIntent: "示范把对象逐个写出，再判断属于关系。",
      studentPrompt: "集合 A={3,6,9,12,15,18} 中，12 是 A 的元素吗？",
      knowledgeAtomIds: ["L5-definition", "L5-method"],
      verifier: { kind: "exact", accepted: ["是", "12属于A", "12∈A"] },
      independence: "guided",
      hints: ["先在花括号中逐个找 12。"],
    },
    {
      id: "L5-diagnose",
      phase: "diagnose",
      title: "分清对象与关系",
      teacherIntent: "依据学生的具体答案诊断属于与包含是否混淆。",
      studentPrompt: "请用“属于”或“不属于”把判断写完整。",
      knowledgeAtomIds: ["L5-method", "L5-misconception"],
      verifier: {
        kind: "structured",
        requiredClaims: ["12属于A"],
        forbiddenClaims: [],
      },
      independence: "guided",
      hints: ["关系左边应当是一个具体数。"],
    },
    {
      id: "L5-summary",
      phase: "fade_scaffold",
      title: "撤去支架",
      teacherIntent: "让学生在迁移前用自己的话复述判断方法。",
      studentPrompt: "用一句话复述：怎样判断一个对象是否属于集合？",
      knowledgeAtomIds: ["L5-method"],
      verifier: {
        kind: "structured",
        requiredClaims: ["核对对象", "在集合中"],
        forbiddenClaims: [],
      },
      independence: "guided",
      hints: ["说清楚要核对的具体对象。"],
    },
    {
      id: "L5-transfer",
      phase: "independent_transfer",
      title: "独立迁移",
      teacherIntent: "撤去提示，验证学生能否独立判断新的对象。",
      studentPrompt: "设 B={4,8,12,16,20}，判断 14 与 B 的关系，并写出理由。",
      knowledgeAtomIds: ["L5-method"],
      verifier: {
        kind: "structured",
        requiredClaims: ["14不属于B", "14不在集合中"],
        forbiddenClaims: [],
      },
      independence: "independent",
      hints: [],
    },
  ],
  sourceCoverage: {
    expectedSourceIds: [
      `${DEMO_SOURCE_ID_PREFIX}placeholder-p12`,
      `${DEMO_SOURCE_ID_PREFIX}placeholder-p13`,
    ],
    coveredSourceIds: [],
    coverageRatio: 0,
  },
};

function summaryLesson(
  id: `L${number}`,
  familyId: string,
  title: string,
  topic: string,
  page: number,
): LessonUnit {
  const atomId = `${id}-definition`;
  return {
    id,
    familyId,
    title,
    topic,
    estimatedMinutes: 30,
    ageRange: [10, 15],
    approvalStatus: "blocked",
    approvalReason: "仅供界面演示；原教材、授权与 canonical 校订均未完成",
    knowledgeAtoms: [
      {
        id: atomId,
        kind: "definition",
        title,
        canonicalText: `${title}的定义、条件与基本方法。`,
        mathStatements: [],
        sourceRefs: [source(page)],
        approvalStatus: "blocked",
      },
    ],
    activities: [
      {
        id: `${id}-explain`,
        phase: "explain",
        title: "概念讲解",
        teacherIntent: "讲清定义、条件与数学对象。",
        knowledgeAtomIds: [atomId],
        independence: "teacher_led",
        hints: [],
      },
      {
        id: `${id}-model`,
        phase: "model",
        title: "方法示范",
        teacherIntent: "示范方法成立的完整理由。",
        knowledgeAtomIds: [atomId],
        independence: "teacher_led",
        hints: [],
      },
      {
        id: `${id}-guided`,
        phase: "guided_practice",
        title: "参与推理",
        teacherIntent: "让学生完成关键一步。",
        studentPrompt: `说出${title}中最关键的判断条件。`,
        knowledgeAtomIds: [atomId],
        independence: "guided",
        hints: ["先找定义中的条件。"],
      },
      {
        id: `${id}-transfer`,
        phase: "independent_transfer",
        title: "独立迁移",
        teacherIntent: "用相近变式验证学生能否独立运用。",
        studentPrompt: `独立完成一道${title}变式，并写出理由。`,
        knowledgeAtomIds: [atomId],
        verifier: {
          kind: "structured",
          requiredClaims: ["理由"],
          forbiddenClaims: [],
        },
        independence: "independent",
        hints: [],
      },
    ],
    sourceCoverage: {
      expectedSourceIds: [`${DEMO_SOURCE_ID_PREFIX}placeholder-p${page}`],
      coveredSourceIds: [],
      coverageRatio: 0,
    },
  };
}

export const demoLessons: LessonUnit[] = [
  setLesson,
  summaryLesson("L17", "functions", "函数与对应", "从变量关系理解函数", 48),
  summaryLesson("L24", "sequences", "等比数列", "识别并推导等比结构", 72),
  summaryLesson(
    "L30",
    "inequalities",
    "含参数不等式",
    "根据条件讨论参数范围",
    91,
  ),
  summaryLesson("L34", "combinatorics", "排列", "从有序选择建立计数方法", 108),
  summaryLesson("L40", "probability", "古典概率", "用等可能样本空间计算概率", 129),
  summaryLesson("L52", "determinants", "二阶行列式", "理解克拉默法则的结构", 165),
  summaryLesson("L57", "complex", "复数的几何意义", "连接复数与平面上的点", 183),
];

export function findDemoLesson(lessonId: string): LessonUnit {
  const lesson = demoLessons.find((candidate) => candidate.id === lessonId);
  if (!lesson) {
    throw new Error(`Demo lesson ${lessonId} was not found`);
  }
  return lesson;
}
